import {
    AfterContentInit,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
    ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { set } from 'lodash';
import { ScrollbarComponent } from 'ngx-scrollbar';
import { from, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { Interaction } from '../../../constants/interaction.constants';
import { KeyCodes } from '../../../constants/keycodes.constants';
import {
    FilterRequest,
    ListResponse,
    Response,
    SearchFilterRequest
} from '@lsq-frontend/remote-services/enterprise/common';
import { ApiService } from '@lsq-frontend/angular-shared/api-service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { Utilities } from '../../../utilities/utilities.helper';
import { BaseFormControlComponent } from '../base-form-control.component';
import { LsqInputComponent } from '../input/lsq-input.component';

@LifecycleAware()
@Component({
    selector: 'lsq-search',
    templateUrl: './lsq-search.component.html',
    styleUrls: ['../input/lsq-input.component.scss', './lsq-search.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LsqSearchComponent),
            multi: true
        }
    ]
})
export class LsqSearchComponent extends BaseFormControlComponent<any>
    implements AfterContentInit, OnDestroy, OnChanges {
    @ViewChild('inputElement', { static: true }) public input: LsqInputComponent;
    @ViewChild('dropdownMenuRef') public dropdownMenuRef: ElementRef;
    @ViewChildren('optionRefs') public optionRefs: QueryList<ElementRef>;
    @ViewChild('scrollBarRef') public scrollRef: ScrollbarComponent;

    @Input() public name: string;
    @Input() public label: string;
    @Input() public group?: FormGroup;
    @Input() public control?: FormControl;
    @Input() public formControlName: string;
    @Input() public basicLabel = false;
    @Input() public floatLabel = false;
    @Input() public innerLabel = true;
    @Input() public placeholder?: string;
    @Input() public readOnly = false;
    @Input() public parentClass?: string;
    @Input() public inputClass?: string;
    @Input() public labelClass?: string;
    @Input() public autoFocus?: boolean;
    @Input() public requestSearchTextFieldName = 'searchFilter';
    @Input() public requestAdditionalFields?: Object = {};

    @Input() public smallTextForResults = false;

    @Input() public searchService?: ApiService<FilterRequest, Response>;
    @Input() public debounceTime = Interaction.SearchDebounceTimeMs;
    @Input() public largeInlineStyling = false;
    @Input() public alwaysShowClose = false;
    @Input() public allowIneligible = false;
    @Input() public primaryDisplayKey: string;
    @Input() public primaryKey: string;
    @Input() public secondaryKey?: string;
    @Input() public secondaryDisplayKey?: string;
    @Input() public secondaryDisplayMethod?: (row: any) => string;
    @Input() public isSecondaryDataAddress = true;

    // Added to handle new filter request objects in Spring
    // TODO: breakout to new component during DS2 Updates for PDEV-224
    @Input() public baseFilterRequest?: FilterRequest;

    @Output() public focus = new EventEmitter();
    @Output() public blur = new EventEmitter();
    @Output() public textChanged = new EventEmitter();
    @Output() public hasResults = new EventEmitter<boolean>();

    private searchUpdated: Subject<string> = new Subject<string>();

    public request = new FilterRequest();
    public response: ListResponse<object>;
    public searchSubscription: Subscription;
    public isFocused = false;
    public icons = Icons;
    public color = Color;
    public ngModelOptions = { updateOn: 'default' };
    public searchText = '';
    public isLoadingResults = false;
    public showClearIcon = false;
    public results: { [key: string]: any }[] = []; // We want this to be dynamic, but expect certain fields.
    public selectedResult: { [key: string]: any }; // Same here.

    public ngAfterContentInit(): void {
        this.inputClass = this.inputClass ? `form-control ${this.inputClass}` : 'form-control';

        this.searchUpdated
            .pipe(forLifeOf(this), debounceTime(this.debounceTime))
            .subscribe((value: string) => {
                if (
                    this.input &&
                    this.input.inputElement &&
                    this.input.inputElement.nativeElement &&
                    this.input.inputElement.nativeElement.value &&
                    this.input.inputElement.nativeElement.value.length > 1
                ) {
                    this.isLoadingResults = true;
                    this.callSearchService(value);
                }
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (!!changes['baseFilterRequest']) {
            if (!!this.baseFilterRequest) {
                this.request = this.baseFilterRequest;
            } else {
                this.request = new SearchFilterRequest({
                    take: -1
                });
            }
        }
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    public handleInputValueChanged(value: string): void {
        this.textChanged.emit();
        this.selectedResult = null;
        if (this.getControl() && this.getControl().value) {
            this.handleChange(null);
            this.getControl().setValue(null);
        }

        this.showClearIcon = value.length > 0;
        if (value && value.length > 1) {
            this.searchUpdated.next(value);
        } else {
            this.resetDropdown();
        }
    }

    public clearSearch(): void {
        this.selectedResult = null;
        this.resetDropdown();
        this.searchText = '';
        if (this.getControl()) {
            this.handleChange(null);
            this.getControl().setValue(null);
        }
    }

    public clearSearchAndValue(): void {
        this.clearSearch();
        this.resetValue();
        this.openInput();
        this.isLoadingResults = false;
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
    }

    private callSearchService(searchFilter: string): void {
        if (!searchFilter || searchFilter.length < 2) {
            this.resetDropdown();
            return;
        }

        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }

        set(this.request, this.requestSearchTextFieldName, searchFilter);

        this.searchSubscription = from(
            this.searchService.executeOnce({ ...this.request, ...this.requestAdditionalFields })
        ).subscribe((response: ListResponse<object>) => {
            this.isLoadingResults = false;
            this.resetDropdown();
            this.hasResults.emit(response.rows && response.rows.length > 0);
            if (this.isFocused && response.rows && response.rows.length > 0) {
                // Handle both the object form and split out form of address.
                response.rows.forEach((row) => {
                    const option: any = row;
                    if (typeof option.address === 'object' || !option.address) {
                        this.results.push(option);
                    } else {
                        this.results.push({
                            ...option,
                            address: {
                                address1: option.address,
                                city: option.city,
                                state: option.state,
                                postalCode: option.zip
                            }
                        });
                    }
                });
                Utilities.executeAfterRender(() => {
                    if (this.scrollRef) {
                        const thumbRef = this.scrollRef.thumbYRef.nativeElement;
                        this.scrollRef.scrollYTo(0, 0);
                        // this fixes a visual bug with the plugin, it resets the scroll thumb.
                        thumbRef.style.transform = 'translate3d(0rem, 0rem, 0rem)';
                    }
                });
            }
        });
    }

    public handleFocusChange(isFocused: boolean): void {
        this.isFocused = isFocused;
        if (this.isFocused) {
            this.focus.emit();
            if (this.selectedResult) {
                this.callSearchService(this.searchText);
            }
        } else {
            this.blur.emit();
            this.resetDropdown();
            this.input.hasInitialBlurHappened = true;
        }
    }

    public setFocus(shouldFocus = true): void {
        if (!!this.input) {
            if (shouldFocus) {
                this.input.inputElement.nativeElement.focus();
            } else {
                this.input.inputElement.nativeElement.blur();
            }
        } else {
            this.openInput();
        }
    }

    public selectItem(selection: any): void {
        this.selectedResult = selection;
        if (this.getControl()) {
            if (!this.secondaryKey) {
                this.handleChange(selection[this.primaryKey]);
                this.getControl().setValue(selection[this.primaryKey]);
            } else {
                this.handleChange({
                    primary: selection[this.primaryKey],
                    secondary: selection[this.secondaryKey]
                });
                this.getControl().setValue({
                    primary: selection[this.primaryKey],
                    secondary: selection[this.secondaryKey]
                });
            }
        }
        this.searchText = this.getSelectedResultPrimaryDisplayText();
        this.handleFocusChange(false);
    }

    public resetDropdown(): void {
        this.results = [];
        this.highlightedOptionIndex = null;
    }

    public resetValue(): void {
        this.showClearIcon = false;
        this.input.handleChange(null);
        this.input.inputElement.nativeElement.value = '';
    }

    public openInput(event?: Event): void {
        this.isFocused = true;
        if (event) {
            event.preventDefault();
        }
        // Wait until the input actaully renders then focus on it.
        Utilities.executeAfterRender(() => {
            this.setFocus();
        });
    }

    public handleViewportKeypress(event: KeyboardEvent, originalIndex?: number): void {
        if (event.which === KeyCodes.UpArrow || event.which === KeyCodes.DownArrow) {
            if (event.which === KeyCodes.UpArrow) {
                event.preventDefault();

                if (!this.highlightedOptionIndex) {
                    this.setHighlightedOption(
                        this.results.length - 1,
                        this.dropdownMenuRef,
                        this.optionRefs,
                        this.scrollRef
                    );
                } else {
                    this.setHighlightedOption(
                        this.highlightedOptionIndex - 1,
                        this.dropdownMenuRef,
                        this.optionRefs,
                        this.scrollRef
                    );
                }
            } else if (event.which === KeyCodes.DownArrow) {
                event.preventDefault();
                if (
                    this.highlightedOptionIndex == null ||
                    this.highlightedOptionIndex === this.results.length - 1
                ) {
                    this.setHighlightedOption(
                        0,
                        this.dropdownMenuRef,
                        this.optionRefs,
                        this.scrollRef
                    );
                } else {
                    this.setHighlightedOption(
                        this.highlightedOptionIndex + 1,
                        this.dropdownMenuRef,
                        this.optionRefs,
                        this.scrollRef
                    );
                }
            }
            if (
                this.results[this.highlightedOptionIndex] &&
                this.results[this.highlightedOptionIndex].isNoBuy &&
                !this.allowIneligible
            ) {
                if (originalIndex !== this.highlightedOptionIndex) {
                    this.handleViewportKeypress(
                        event,
                        originalIndex || this.highlightedOptionIndex
                    );
                } else {
                    this.highlightedOptionIndex = null;
                }
            }
        } else if (event.which === KeyCodes.Enter) {
            event.preventDefault();
            if (this.results.length > 0 && this.highlightedOptionIndex !== null) {
                this.selectItem(this.results[this.highlightedOptionIndex]);
                this.handleFocusChange(false);
            }
        } else if (event.which === KeyCodes.Escape) {
            this.resetDropdown();
        }
    }

    // Correct way to retrieve the name since it may be updated next cycle when selecting.
    public getSelectedResultPrimaryDisplayText(): string {
        if (this.selectedResult && this.selectedResult[this.primaryDisplayKey]) {
            return this.selectedResult[this.primaryDisplayKey];
        }
        return '';
    }

    // Jumper these ControlValueAccessor functions to the underlying lsq-input.
    public writeValue(model: string): void {
        this.input.writeValue(model);
    }

    public registerOnChange(fn: (_: string) => void): void {
        this.input.registerOnChange(fn);
    }

    public registerOnTouched(fn: () => void): void {
        this.input.registerOnTouched(fn);
    }
}
